import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    ArrowLeft,
    Calculator,
    TrendingUp,
    TrendingDown,
    DollarSign,
    BarChart3,
    PieChart,
    FileText,
    Download,
    Printer,
    RefreshCw,
    Ship,
    Package,
    Calendar,
    User,
    Building2,
    MapPin,
    Receipt,
    Link2,
    Unlink,
    ExternalLink,
    AlertCircle,
    CheckCircle2,
    Clock,
    FileCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getBookingDetails } from "@/redux/Booking/actions";
import { getArrivalNoticeList } from "@/redux/ImportBooking/actions";
import {
    getJobCostingMode,
    JOB_COSTING_MODE,
    resolveBookingPayload,
    extractLinkedDocuments,
} from "./JobCosting/jobCostingUtils";
import {
    getAccountingDivision,
    buildBookingAccountingStatusUrl,
    mapAccountingStatusOntoCharges,
} from "@/utils/accountingBookingUtils";
import { baseURL } from "@/config/APIConfig";

const API_URL = baseURL.AiBaseURL;
import { callLoader } from "@/redux/Loader/actions";
import * as Tabs from "@radix-ui/react-tabs";
import { ChargeSelectionDialog } from "./JobCostingComponants/ChargeSelectionDialog";
import { toast } from "sonner";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AIInsightCard from "@/components/shipment/AIInsightCard";

// Status badge configurations
const STATUS_CONFIGS = {
    estimated: {
        label: "Estimated",
        variant: "outline",
        className: "border-slate-400 text-slate-600 dark:border-slate-500 dark:text-slate-400",
        icon: Clock,
    },
    draft: {
        label: "Draft",
        variant: "secondary",
        className: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
        icon: FileText,
    },
    issued: {
        label: "Issued",
        variant: "default",
        className: "bg-blue-500 text-white hover:bg-blue-600",
        icon: FileCheck,
    },
    paid: {
        label: "Paid",
        variant: "default",
        className: "bg-green-500 text-white hover:bg-green-600",
        icon: CheckCircle2,
    },
};

// Get status config for charge
const getChargeStatus = (charge, type) => {
    if (type === "AR") {
        if (charge.paymentStatus === "paid") return "paid";
        if (charge.invoiceStatus === "issued" || charge.invoiceStatus === "invoiced") return "issued";
        if (charge.invoiceStatus === "draft") return "draft";
        return "estimated";
    } else {
        if (charge.paymentStatus === "paid") return "paid";
        if (charge.billStatus === "issued" || charge.billStatus === "billed") return "issued";
        if (charge.billStatus === "draft") return "draft";
        return "estimated";
    }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIGS[status] || STATUS_CONFIGS.estimated;
    const Icon = config.icon;

    return (
        <Badge variant={config.variant} className={`${config.className} gap-1 text-xs`}>
            <Icon className="w-3 h-3" />
            {config.label}
        </Badge>
    );
};

// Calculate USD equivalent with ROE
const calculateUSDEquivalent = (charge) => {
    const quantity = parseFloat(charge.quantity) || 1;
    const amount = parseFloat(charge.amount) || 0;
    const roe = parseFloat(charge.roe) || 1;

    if (charge.currency === "USD" || !charge.currency) {
        return quantity * amount;
    }
    return (quantity * amount) / roe;
};

const JobCosting = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const mode = getJobCostingMode(location.pathname, location.search);
    const isImport = mode === JOB_COSTING_MODE.IMPORT;

    const [bookingData, setBookingData] = useState(null);
    const [arCharges, setArCharges] = useState([]);
    const [apCharges, setApCharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("all");
    const [open, setOpen] = useState("");
    const [selectedParty, setSelectedParty] = useState(null);
    const [unlinkDialog, setUnlinkDialog] = useState({ open: false, charge: null, type: null });
    const [linkedInvoices, setLinkedInvoices] = useState([]);
    const [linkedBills, setLinkedBills] = useState([]);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const companyId = authUser?.companyId;
    const division = getAccountingDivision(mode);

    const fetchAccountingStatus = async (ar, ap) => {

        console.log("=========ar=========== :", ar);
        console.log("=========ap=========== :", ap);
        if (!bookingId || !companyId) return { ar, ap };

        try {
            const url = buildBookingAccountingStatusUrl(
                API_URL,
                bookingId,
                companyId,
                division
            );
            console.log("=========url=========== :", url);
            const response = await fetch(url);

            console.log("=========response=========== :", response);
            if (!response.ok) return { ar, ap };

            const data = await response.json();
            console.log("=========data=========== :", data);
            const mapped = mapAccountingStatusOntoCharges(ar, ap, data);
            console.log("=========mapped=========== :", mapped,);
            const { linkedInvoices, linkedBills } = extractLinkedDocuments(
                mapped.arCharges,
                mapped.apCharges
            );
            console.log("=========linkedInvoices=========== :", linkedInvoices);
            console.log("=========linkedBills=========== :", linkedBills);
            setLinkedInvoices(linkedInvoices);
            setLinkedBills(linkedBills);
            return mapped;
        } catch (error) {
            console.error("Error fetching accounting status:", error);
            return { ar, ap };
        }
    };

    useEffect(() => {
        if (bookingId) {
            loadBookingData();
        }
    }, [bookingId, mode, companyId]);

    const loadBookingData = async () => {
        setLoading(true);
        dispatch(callLoader(1));
        try {
            const data = isImport
                ? await dispatch(
                    getArrivalNoticeList({
                        BolID: bookingId,
                        CompanyID: authUser?.companyId,
                    })
                )
                : await dispatch(getBookingDetails(bookingId, authUser?.companyId));

            if (data) {
                const { booking, arCharges: ar, apCharges: ap } = resolveBookingPayload(data, mode);
                setBookingData(booking);

                const mapped = await fetchAccountingStatus(ar, ap);
                setArCharges(mapped.arCharges);
                setApCharges(mapped.apCharges);

                if (!companyId) {
                    const { linkedInvoices, linkedBills } = extractLinkedDocuments(ar, ap);
                    setLinkedInvoices(linkedInvoices);
                    setLinkedBills(linkedBills);
                }
            }
        } catch (error) {
            console.error("Error loading booking:", error);
            toast.error("Failed to load booking data");
        } finally {
            setLoading(false);
            dispatch(callLoader(0));
        }
    };

    // Group AR charges by bill-to-party
    const arChargesByParty = useMemo(() => {
        const grouped = {};
        arCharges.forEach(charge => {
            const party = charge.billToParty || "Unknown";
            if (!grouped[party]) {
                grouped[party] = {
                    partyName: party,
                    partyId: charge.billToPartyId,
                    billToPartyRef: charge.billToPartyRef,
                    charges: [],
                    total: 0,
                    totalUSD: 0,
                };
            }
            grouped[party].charges.push(charge);
            grouped[party].total += (parseFloat(charge.amount) || 0) * (parseFloat(charge.quantity) || 1);
            grouped[party].totalUSD += calculateUSDEquivalent(charge);
        });
        return Object.values(grouped);
    }, [arCharges]);

    // Group AP charges by vendor
    const apChargesByVendor = useMemo(() => {
        const grouped = {};
        apCharges.forEach(charge => {
            const vendor = charge.vendor || "Unknown";
            if (!grouped[vendor]) {
                grouped[vendor] = {
                    vendorName: vendor,
                    vendorId: charge.vendorId,
                    vendorRef: charge.vendorRef,
                    charges: [],
                    total: 0,
                    totalUSD: 0,
                };
            }
            grouped[vendor].charges.push(charge);
            grouped[vendor].total += (parseFloat(charge.amount) || 0) * (parseFloat(charge.quantity) || 1);
            grouped[vendor].totalUSD += calculateUSDEquivalent(charge);
        });
        return Object.values(grouped);
    }, [apCharges]);

    // Calculate totals using USD equivalents
    const totalRevenue = useMemo(() =>
        arCharges.reduce((sum, c) => sum + calculateUSDEquivalent(c), 0),
        [arCharges]
    );

    const totalCost = useMemo(() =>
        apCharges.reduce((sum, c) => sum + calculateUSDEquivalent(c), 0),
        [apCharges]
    );

    const grossProfit = totalRevenue - totalCost;
    const profitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

    // Margin analysis by charge code
    const chargeMarginAnalysis = useMemo(() => {
        const analysis = {};

        arCharges.forEach(charge => {
            const code = charge.chargeCode || charge.description || "OTHER";
            if (!analysis[code]) {
                analysis[code] = { code, description: charge.description || code, ar: 0, ap: 0 };
            }
            analysis[code].ar += calculateUSDEquivalent(charge);
        });

        apCharges.forEach(charge => {
            const code = charge.chargeCode || charge.description || "OTHER";
            if (!analysis[code]) {
                analysis[code] = { code, description: charge.description || code, ar: 0, ap: 0 };
            }
            analysis[code].ap += calculateUSDEquivalent(charge);
        });

        return Object.values(analysis)
            .map(item => ({
                ...item,
                profit: item.ar - item.ap,
                margin: item.ar > 0 ? ((item.ar - item.ap) / item.ar) * 100 : item.ap > 0 ? -100 : 0,
            }))
            .sort((a, b) => b.profit - a.profit);
    }, [arCharges, apCharges]);

    // Handle opening charge selection dialog
    const handleOpenChargeSelection = (type, party) => {
        setSelectedParty(party);
        setOpen(type);
    };

    // Handle proceeding to create invoice/bill
    const handleProceedToAccounting = (selectedChargeIds) => {
        if (!open || !bookingId || !selectedParty) return;

        console.log("=========selected party=========== :", selectedParty);
        console.log("=========selected id=========== :", selectedChargeIds);
        const charges = open === "AR" ? selectedParty.charges : selectedParty.charges;
        const selectedCharges = charges.filter(c => selectedChargeIds.includes(c.id));

        const prefillData = {
            ...bookingData,
            bookingId: bookingId,
            id: bookingId,
            fileNumber: bookingData?.fileNumber || bookingData?.bookingNumber,
            company_id: companyId,
            division,
            charges: selectedCharges.map((c, i) => ({
                ...c,
                line_no: i + 1,
                description: c.description,
                quantity: Number(c?.quantity) || 1,
                unit_price: Number(c?.amount) || 0,
                amount: (Number(c?.quantity) || 1) * (Number(c?.amount) || 0),
                tax_rate: 0,
                tax_amount: 0,
                total_amount: (Number(c?.quantity) || 1) * (Number(c?.amount) || 0),
                roe: c?.roe || 1,
                currency: c?.currency || "USD",
                chargeId: c.id, // Link back to original charge
            })),
        };

        // console.log("data going to account:", prefillData);
        if (open === "AR") {
            prefillData.billToParty = selectedParty.partyName;
            prefillData.billToPartyId = selectedParty.partyId;
            prefillData.billToPartyRef = selectedParty.billToPartyRef;
            navigate("/accounting/invoices", {
                state: { prefillData, fromJobCosting: true, companyId, division },
            });
        } else {
            prefillData.vendor = selectedParty.vendorName;
            prefillData.vendorId = selectedParty.vendorId;
            prefillData.vendorRef = selectedParty.vendorRef;
            navigate("/accounting/bills", {
                state: { prefillData, fromJobCosting: true, companyId, division },
            });
        }

        // console.log("Prefill data for accounting:", prefillData);
        setOpen("");
        setSelectedParty(null);
    };

    // Handle unlink invoice/bill from charge
    const handleUnlink = async () => {
        const { charge, type } = unlinkDialog;
        if (!charge) return;

        try {
            // In a real implementation, this would call an API to update the booking snapshot
            // For now, we'll update the local state
            if (type === "AR") {
                setArCharges(prev => prev.map(c =>
                    c.id === charge.id
                        ? { ...c, invoiceStatus: "estimated", invoiceNo: null, invoiceId: null }
                        : c
                ));
            } else {
                setApCharges(prev => prev.map(c =>
                    c.id === charge.id
                        ? { ...c, billStatus: "estimated", billNo: null, billId: null }
                        : c
                ));
            }

            toast.success(`Successfully unlinked ${type === "AR" ? "invoice" : "bill"} from charge`);
        } catch (error) {
            toast.error("Failed to unlink. Please try again.");
        } finally {
            setUnlinkDialog({ open: false, charge: null, type: null });
        }
    };

    // Helper functions
    const getLocationName = (location) => {
        if (!location) return "...";
        const parts = location.split(",");
        return parts[0].replace(/\[.*\]/g, "").trim();
    };

    const getLocationCode = (location) => {
        if (!location) return "";
        const match = location.match(/\[(.*?)\]/);
        return match ? match[1] : "";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "NA";
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    const formatCurrency = (amount, currency = "USD") => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const financialCards = useMemo(
        () => [
            {
                icon: TrendingUp,
                title: `${arCharges.length} AR charges`,
                value: formatCurrency(totalRevenue),
                trend: "Revenue",
                showTrendIcon: false,
                accentKey: "amber",
            },
            {
                icon: TrendingDown,
                title: `${apCharges.length} AP charges`,
                value: formatCurrency(totalCost),
                trend: "Cost",
                showTrendIcon: false,
                accentKey: "rose",
            },
            {
                icon: DollarSign,
                title: grossProfit >= 0 ? "Profitable" : "Loss",
                value: formatCurrency(grossProfit),
                trend: "Gross Profit",
                showTrendIcon: false,
                accentKey: grossProfit >= 0 ? "emerald" : "red",
            },
            {
                icon: PieChart,
                title: "Profit Margin",
                value: `${profitMargin.toFixed(1)}%`,
                trend: "Margin %",
                showTrendIcon: false,
                accentKey: profitMargin >= 0 ? "blue" : "red",
            },
        ],
        [
            totalRevenue,
            totalCost,
            grossProfit,
            profitMargin,
            arCharges.length,
            apCharges.length,
        ]
    );

    // if (loading) {
    //     return (
    //         <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
    //             <div className="flex items-center gap-3">
    //                 <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
    //                 <span className="text-slate-600 dark:text-slate-400">
    //                     Loading job costing data...
    //                 </span>
    //             </div>
    //         </div>
    //     );
    // }

    return (
        <TooltipProvider>
            <div className="min-h-screen bg-slate-200 dark:bg-slate-900 py-4 px-0 pb-24">
                <div className="w-full px-4 space-y-6">
                    {/* Header */}
                    <div className="bg-white dark:bg-slate-900
                        border border-ink-100 dark:border-slate-700 rounded-2xl shadow-card dark:shadow-gray-900/40 px-6 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    data-testid="back-btn"
                                >
                                    <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                </button>
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                                        <Calculator className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <div>
                                        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">
                                            Job Costing / P&L Synopsis
                                        </h1>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            {isImport ? "Import" : "Export"} Booking #{bookingData?.fileNumber || bookingData?.bookingNumber || bookingId}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={loadBookingData}
                                    className="text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                                    data-testid="refresh-btn"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </Button>
                                <Button variant="outline" size="sm" data-testid="export-btn"
                                    className="text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Export
                                </Button>
                                <Button variant="outline" size="sm" data-testid="print-btn"
                                    className="text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                                >
                                    <Printer className="w-4 h-4 mr-2" />
                                    Print
                                </Button>
                            </div>
                        </div>

                        {/* Booking Info Summary */}
                        {bookingData && (
                            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="flex items-center gap-2 text-sm">
                                    <User className="w-4 h-4 text-slate-400" />
                                    <span className="text-slate-600 dark:text-slate-400">Customer:</span>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">
                                        {bookingData.customerName || "N/A"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Ship className="w-4 h-4 text-slate-400" />
                                    <span className="text-slate-600 dark:text-slate-400">Carrier:</span>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">
                                        {bookingData.carrier || "N/A"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="w-4 h-4 text-slate-400" />
                                    <span className="text-slate-600 dark:text-slate-400">Route:</span>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">
                                        {getLocationCode(bookingData?.portOfLoading) || bookingData?.portOfLoadingCode || "..."}
                                        {" → "}
                                        {getLocationCode(bookingData?.portOfDischarge) || bookingData?.portOfDischargeCode || "..."}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    <span className="text-slate-600 dark:text-slate-400">ETD:</span>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">
                                        {formatDate(bookingData?.etsAtPol)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* P&L Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        {financialCards.map((card) => (
                            <AIInsightCard
                                key={card.title}
                                {...card}
                            />
                        ))}
                    </div>

                    {/* Linked Documents Section */}
                    {(linkedInvoices.length > 0 || linkedBills.length > 0) && (
                        <div className="bg-white dark:bg-slate-900
                                            border border-ink-100 dark:border-slate-700 rounded-2xl shadow-card dark:shadow-gray-900/40 p-6">
                            <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
                                <Link2 className="w-5 h-5" />
                                Linked Documents
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {linkedInvoices.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium text-amber-600">Invoices</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {linkedInvoices.map(inv => (
                                                <Badge
                                                    key={inv.invoiceNo}
                                                    variant="outline"
                                                    className="cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                                    // onClick={() => navigate(`/accounting/invoices/${inv.id}`)}
                                                    onClick={() => navigate(`/accounting/invoices`)}
                                                >
                                                    <FileText className="w-3 h-3 mr-1" />
                                                    {inv.invoiceNo}
                                                    <ExternalLink className="w-3 h-3 ml-1" />
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {linkedBills.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium text-green-600">Vendor Bills</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {linkedBills.map(bill => (
                                                <Badge
                                                    key={bill.billNo}
                                                    variant="outline"
                                                    className="cursor-pointer hover:bg-green-50 dark:hover:bg-green-900"
                                                    onClick={() => navigate(`/accounting/bills`)}
                                                >
                                                    <Receipt className="w-3 h-3 mr-1" />
                                                    {bill.billNo}
                                                    <ExternalLink className="w-3 h-3 ml-1" />
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tabs */}
                    <div>
                        <Tabs.Root value={tab} onValueChange={setTab} className="w-full max-w-md">
                            <Tabs.List className="grid w-full grid-cols-3 h-10 items-center rounded-md bg-white p-1 text-muted-foreground  border border-gray-200 dark:border-gray-800 dark:bg-gray-900 shadow-sm dark:shadow-gray-900/40">
                                <Tabs.Trigger
                                    value="all"
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm"
                                >
                                    All
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="ar"
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm"
                                >
                                    Revenue (AR)
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="ap"
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm"
                                >
                                    Cost (AP)
                                </Tabs.Trigger>
                            </Tabs.List>
                        </Tabs.Root>
                    </div>

                    {/* Charge Tables - Grouped by Party/Vendor */}
                    <div className={`grid grid-cols-1 ${tab !== "all" ? "lg:grid-cols-1" : "lg:grid-cols-1"} gap-6`}>
                        {/* AR Charges - Grouped by Bill-To Party */}
                        {tab !== "ap" && (
                            <div className="space-y-4">
                                {/* Section Header */}
                                <div
                                    className="bg-white dark:bg-slate-900
                       border border-ink-100 dark:border-slate-700
                       shadow-card dark:shadow-slate-900/40
                       rounded-2xl px-4 py-3
                       flex items-center gap-3"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5" />
                                    </div>

                                    <div>
                                        <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                                            Revenue (AR) - By Bill-To Party
                                        </h3>

                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Revenue grouped by customer billing party
                                        </p>
                                    </div>
                                </div>

                                {/* Empty State */}
                                {arChargesByParty.length === 0 ? (
                                    <div
                                        className="rounded-2xl bg-white dark:bg-slate-900
                           border border-ink-100 dark:border-slate-700
                           shadow-card dark:shadow-slate-900/40
                           p-10 text-center"
                                    >
                                        <div className="bg-white dark:bg-slate-900 rounded-b-xl p-8 text-center text-slate-500">
                                            No AR charges recorded
                                        </div>
                                    </div>
                                ) : (
                                    arChargesByParty.map((party, partyIndex) => (
                                        <div
                                            key={partyIndex}
                                            className="rounded-2xl bg-white dark:bg-slate-900
                               border border-ink-100 dark:border-slate-700
                               shadow-card dark:shadow-slate-900/40
                               overflow-hidden"
                                        >
                                            {/* Party Header */}
                                            <div
                                                className="border-b border-ink-100 dark:border-slate-700
                                   px-4 py-3
                                   flex flex-col lg:flex-row lg:items-center lg:justify-between
                                   gap-3"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
                                                        <Building2 className="w-4 h-4" />
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                                Bill To: {party.partyName}
                                                            </span>

                                                            <Badge
                                                                variant="outline"
                                                                className="text-[11px]"
                                                            >
                                                                {party.charges.length} charges
                                                            </Badge>
                                                        </div>

                                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                            Customer revenue breakdown
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                                            Total Revenue
                                                        </div>

                                                        <div className="font-bold text-amber-600 dark:text-amber-400 text-lg">
                                                            {formatCurrency(party.totalUSD)}
                                                        </div>
                                                    </div>

                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            handleOpenChargeSelection("AR", party)
                                                        }
                                                        className="
                                    text-slate-600 hover:bg-amber-500
                                    hover:text-white hover:border-amber-500
                                    dark:text-slate-300
                                "
                                                        data-testid={`create-invoice-btn-${partyIndex}`}
                                                    >
                                                        <Receipt className="w-3.5 h-3.5 mr-1.5" />
                                                        Create Invoice
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Table */}
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-[12.5px]">
                                                    <thead
                                                        className="
                                    sticky top-0 z-10
                                    bg-ink-100/60 dark:bg-slate-800/70
                                    backdrop-blur
                                    text-[10.5px] uppercase tracking-[0.14em]
                                    text-ink-500 dark:text-slate-400
                                "
                                                    >
                                                        <tr>
                                                            <th className="text-left font-semibold px-3 py-3">
                                                                #
                                                            </th>

                                                            <th className="text-left font-semibold px-3 py-3">
                                                                Description
                                                            </th>

                                                            <th className="text-center font-semibold px-3 py-3">
                                                                Currency
                                                            </th>

                                                            <th className="text-center font-semibold px-3 py-3">
                                                                Qty
                                                            </th>

                                                            <th className="text-right font-semibold px-3 py-3">
                                                                Rate
                                                            </th>

                                                            <th className="text-center font-semibold px-3 py-3">
                                                                ROE
                                                            </th>

                                                            <th className="text-right font-semibold px-3 py-3">
                                                                USD Equiv.
                                                            </th>

                                                            <th className="text-center font-semibold px-3 py-3">
                                                                PP/CC
                                                            </th>

                                                            <th className="text-center font-semibold px-3 py-3">
                                                                Status
                                                            </th>

                                                            <th className="text-center font-semibold px-3 py-3">
                                                                Link
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {party.charges.map((charge, index) => {
                                                            const status = getChargeStatus(
                                                                charge,
                                                                "AR"
                                                            );

                                                            return (
                                                                <tr
                                                                    key={charge.id}
                                                                    className={[
                                                                        "border-t border-ink-100 dark:border-slate-700/50",
                                                                        "transition-colors hover:bg-blue-50/30 dark:hover:bg-blue-900/10",
                                                                        index % 2 === 1
                                                                            ? "bg-ink-100/20 dark:bg-slate-800/30"
                                                                            : "",
                                                                    ].join(" ")}
                                                                >
                                                                    {/* Index */}
                                                                    <td className="px-3 py-3 text-slate-500 dark:text-slate-400 tabular-nums">
                                                                        {index + 1}
                                                                    </td>

                                                                    {/* Description */}
                                                                    <td className="px-3 py-3">
                                                                        <div className="font-semibold text-ink-900 dark:text-white">
                                                                            {charge.description || "-"}
                                                                        </div>
                                                                    </td>

                                                                    {/* Currency */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        <span className="text-ink-700 dark:text-slate-300 font-medium">
                                                                            {charge.currency || "USD"}
                                                                        </span>
                                                                    </td>

                                                                    {/* Qty */}
                                                                    <td className="px-3 py-3 text-center tabular-nums text-ink-700 dark:text-slate-300">
                                                                        {charge.quantity || 1}
                                                                    </td>

                                                                    {/* Rate */}
                                                                    <td className="px-3 py-3 text-right tabular-nums text-ink-700 dark:text-slate-300">
                                                                        {parseFloat(
                                                                            charge.amount || 0
                                                                        ).toFixed(2)}
                                                                    </td>

                                                                    {/* ROE */}
                                                                    <td className="px-3 py-3 text-center tabular-nums text-ink-700 dark:text-slate-300">
                                                                        {charge.roe || "1.00"}
                                                                    </td>

                                                                    {/* USD */}
                                                                    <td className="px-3 py-3 text-right">
                                                                        <span className="font-semibold tabular-nums text-blue-700 dark:text-blue-400">
                                                                            {formatCurrency(
                                                                                calculateUSDEquivalent(
                                                                                    charge
                                                                                )
                                                                            )}
                                                                        </span>
                                                                    </td>

                                                                    {/* PP/CC */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        <span
                                                                            className="
                                                        inline-flex items-center justify-center
                                                        min-w-[34px] h-6 px-2 rounded-md
                                                        bg-slate-100 dark:bg-slate-700/60
                                                        text-[11px] font-semibold
                                                        text-slate-700 dark:text-slate-300
                                                    "
                                                                        >
                                                                            {charge.ppcc || "P"}
                                                                        </span>
                                                                    </td>

                                                                    {/* Status */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        <StatusBadge status={status} />
                                                                    </td>

                                                                    {/* Invoice */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        {charge.invoiceNo ? (
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <div className="flex items-center justify-center gap-1.5">
                                                                                        <Badge
                                                                                            variant="outline"
                                                                                            className="
                                                                        h-7 px-2.5 rounded-md
                                                                        border-blue-200 dark:border-blue-800
                                                                        bg-blue-50/70 dark:bg-blue-900/10
                                                                        text-blue-700 dark:text-blue-300
                                                                        hover:bg-blue-100 dark:hover:bg-blue-900/20
                                                                        cursor-pointer transition-colors
                                                                    "
                                                                                            onClick={() =>
                                                                                                charge.invoiceId &&
                                                                                                navigate(
                                                                                                    `/accounting/invoices/${charge.invoiceId}`
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                charge.invoiceNo
                                                                                            }
                                                                                        </Badge>

                                                                                        <Button
                                                                                            size="icon"
                                                                                            variant="ghost"
                                                                                            className="
                                                                        h-7 w-7 rounded-md
                                                                        text-slate-400
                                                                        hover:bg-red-50
                                                                        hover:text-red-500
                                                                        dark:hover:bg-red-900/20
                                                                    "
                                                                                            onClick={() =>
                                                                                                setUnlinkDialog(
                                                                                                    {
                                                                                                        open: true,
                                                                                                        charge,
                                                                                                        type: "AR",
                                                                                                    }
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <Unlink className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    </div>
                                                                                </TooltipTrigger>

                                                                                <TooltipContent>
                                                                                    Click to view invoice
                                                                                    or unlink
                                                                                </TooltipContent>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <span className="text-slate-400">
                                                                                -
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* AP Charges - Grouped by Vendor */}
                        {tab !== "ar" && (
                            <div className="space-y-4">
                                {/* Section Header */}
                                <div
                                    className="bg-white dark:bg-slate-900
                       border border-ink-100 dark:border-slate-700
                       shadow-card dark:shadow-slate-900/40
                       px-4 py-3 rounded-t-xl flex items-center gap-2"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
                                        <TrendingDown className="w-5 h-5" />
                                    </div>

                                    <h3 className="font-semibold text-slate-800 dark:text-slate-200">
                                        Cost (AP) - By Vendor
                                    </h3>
                                </div>

                                {apChargesByVendor.length === 0 ? (
                                    <div className="bg-white dark:bg-slate-900 rounded-b-xl p-8 text-center text-slate-500">
                                        No AP charges recorded
                                    </div>
                                ) : (
                                    apChargesByVendor.map((vendor, vendorIndex) => (
                                        <div
                                            key={vendorIndex}
                                            className="rounded-2xl bg-white dark:bg-slate-900
                               border border-ink-100 dark:border-slate-700
                               shadow-card dark:shadow-slate-900/40
                               overflow-hidden"
                                        >
                                            {/* Vendor Header */}
                                            <div className="border-b border-ink-100 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
                                                        <Building2 className="w-4 h-4" />
                                                    </div>

                                                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                        Vendor: {vendor.vendorName}
                                                    </span>

                                                    <Badge variant="outline" className="text-xs">
                                                        {vendor.charges.length} charges
                                                    </Badge>

                                                    {vendor.vendorRef && (
                                                        <span className="text-xs text-slate-500 dark:text-slate-400">
                                                            Ref: {vendor.vendorRef}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                                        {formatCurrency(vendor.totalUSD)}
                                                    </span>

                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleOpenChargeSelection("AP", vendor)}
                                                        className="text-slate-600 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 dark:text-slate-300"
                                                        data-testid={`create-bill-btn-${vendorIndex}`}
                                                    >
                                                        <Receipt className="w-3 h-3 mr-1" />
                                                        Create Bill
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Charges Table */}
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-xs">
                                                    <thead
                                                        className="sticky top-0 z-10
                                           bg-ink-100/60 dark:bg-slate-800/70
                                           backdrop-blur
                                           text-[10.5px] uppercase tracking-[0.14em]
                                           text-ink-500 dark:text-slate-400"
                                                    >
                                                        <tr className="bg-slate-50 dark:bg-slate-700">
                                                            <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                                                                #
                                                            </th>

                                                            <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                                                                Description
                                                            </th>

                                                            <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">
                                                                Currency
                                                            </th>

                                                            <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">
                                                                Qty
                                                            </th>

                                                            <th className="px-3 py-3 text-right font-medium text-slate-600 dark:text-slate-300">
                                                                Rate
                                                            </th>

                                                            <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">
                                                                ROE
                                                            </th>

                                                            <th className="px-3 py-3 text-right font-medium text-slate-600 dark:text-slate-300">
                                                                USD Equiv.
                                                            </th>

                                                            <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">
                                                                PP/CC
                                                            </th>

                                                            <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">
                                                                Status
                                                            </th>

                                                            <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">
                                                                Link
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {vendor.charges.map((charge, index) => {
                                                            const status = getChargeStatus(charge, "AP");

                                                            return (
                                                                <tr
                                                                    key={charge.id}
                                                                    className={`border-b border-slate-100 dark:border-slate-700
                                            ${index % 2 === 0
                                                                            ? "bg-white dark:bg-slate-800"
                                                                            : "bg-slate-50/50 dark:bg-slate-800/50"
                                                                        }
                                            hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10
                                            transition-colors`}
                                                                >
                                                                    {/* Index */}
                                                                    <td className="px-3 py-3 text-slate-600 dark:text-slate-400 tabular-nums">
                                                                        {index + 1}
                                                                    </td>

                                                                    {/* Description */}
                                                                    <td className="px-3 py-3 font-medium text-slate-700 dark:text-slate-200">
                                                                        {charge.description || "-"}
                                                                    </td>

                                                                    {/* Currency */}
                                                                    <td className="px-3 py-3 text-center text-slate-700 dark:text-slate-300">
                                                                        {charge.currency || "USD"}
                                                                    </td>

                                                                    {/* Qty */}
                                                                    <td className="px-3 py-3 text-center text-slate-700 dark:text-slate-300">
                                                                        {charge.quantity || 1}
                                                                    </td>

                                                                    {/* Rate */}
                                                                    <td className="px-3 py-3 text-right text-slate-700 dark:text-slate-300">
                                                                        {parseFloat(charge.amount || 0).toFixed(2)}
                                                                    </td>

                                                                    {/* ROE */}
                                                                    <td className="px-3 py-3 text-center text-slate-700 dark:text-slate-300">
                                                                        {charge.roe || "1.00"}
                                                                    </td>

                                                                    {/* USD */}
                                                                    <td className="px-3 py-3 text-right font-medium text-emerald-600 dark:text-emerald-400">
                                                                        {formatCurrency(
                                                                            calculateUSDEquivalent(charge)
                                                                        )}
                                                                    </td>

                                                                    {/* PP/CC */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        <span
                                                                            className="inline-flex items-center justify-center
                                                               min-w-[34px] h-6 px-2 rounded-md
                                                               bg-slate-100 dark:bg-slate-700/60
                                                               text-[11px] font-semibold
                                                               text-slate-700 dark:text-slate-300"
                                                                        >
                                                                            {charge.ppcc || "P"}
                                                                        </span>
                                                                    </td>

                                                                    {/* Status */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        <StatusBadge status={status} />
                                                                    </td>

                                                                    {/* Bill Link */}
                                                                    <td className="px-3 py-3 text-center">
                                                                        {charge.billNo ? (
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <div className="flex items-center justify-center gap-1.5">
                                                                                        <Badge
                                                                                            variant="outline"
                                                                                            className="
                                                                        h-7 px-2.5 rounded-md
                                                                        border-emerald-200 dark:border-emerald-800
                                                                        bg-emerald-50/70 dark:bg-emerald-900/10
                                                                        text-emerald-700 dark:text-emerald-300
                                                                        hover:bg-emerald-100 dark:hover:bg-emerald-900/20
                                                                        cursor-pointer transition-colors
                                                                    "
                                                                                            onClick={() =>
                                                                                                charge.billId &&
                                                                                                navigate(`/accounting/bills`)
                                                                                            }
                                                                                        >
                                                                                            {charge.billNo}
                                                                                        </Badge>

                                                                                        <Button
                                                                                            size="icon"
                                                                                            variant="ghost"
                                                                                            className="
                                                                        h-7 w-7 rounded-md
                                                                        text-slate-400
                                                                        hover:bg-red-50
                                                                        hover:text-red-500
                                                                        dark:hover:bg-red-900/20
                                                                    "
                                                                                            onClick={() =>
                                                                                                setUnlinkDialog({
                                                                                                    open: true,
                                                                                                    charge,
                                                                                                    type: "AP",
                                                                                                })
                                                                                            }
                                                                                        >
                                                                                            <Unlink className="h-3.5 w-3.5" />
                                                                                        </Button>
                                                                                    </div>
                                                                                </TooltipTrigger>

                                                                                <TooltipContent>
                                                                                    Click to view bill or unlink
                                                                                </TooltipContent>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <span className="text-slate-400">
                                                                                -
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Margin Analysis Table */}
                    <div
                        className="rounded-2xl bg-white dark:bg-slate-900
               border border-ink-100 dark:border-slate-700
               shadow-card dark:shadow-slate-900/40
               overflow-hidden"
                    >
                        {/* Header */}
                        <div className="border-b border-ink-100 dark:border-slate-700 px-4 py-4">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
                                    <BarChart3 className="w-5 h-5" />
                                </div>

                                <h3 className="font-semibold text-slate-800 dark:text-slate-200">
                                    Margin Analysis by Charge Type
                                </h3>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-[12.5px]">
                                <thead
                                    className="sticky top-0 z-10
                           bg-ink-100/60 dark:bg-slate-800/70
                           backdrop-blur
                           text-[10.5px] uppercase tracking-[0.14em]
                           text-ink-500 dark:text-slate-400"
                                >
                                    <tr className="bg-slate-50 dark:bg-slate-700">
                                        <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                                            Description
                                        </th>

                                        <th className="px-4 py-3 text-right font-medium text-amber-600 dark:text-amber-400">
                                            AR (Revenue)
                                        </th>

                                        <th className="px-4 py-3 text-right font-medium text-emerald-600 dark:text-emerald-400">
                                            AP (Cost)
                                        </th>

                                        <th className="px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">
                                            Profit
                                        </th>

                                        <th className="px-4 py-3 text-right font-medium text-purple-600 dark:text-purple-400">
                                            Margin %
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {chargeMarginAnalysis.map((item, index) => (
                                        <tr
                                            key={item.code}
                                            className={`border-b border-slate-100 dark:border-slate-700
                        ${index % 2 === 0
                                                    ? "bg-white dark:bg-slate-800"
                                                    : "bg-slate-50/50 dark:bg-slate-800/50"
                                                }
                        hover:bg-blue-50/30 dark:hover:bg-blue-900/10
                        transition-colors`}
                                        >
                                            {/* Description */}
                                            <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-200">
                                                {item.description}
                                            </td>

                                            {/* AR */}
                                            <td className="px-4 py-3 text-right">
                                                <span className="font-semibold tabular-nums text-amber-600 dark:text-amber-400">
                                                    {formatCurrency(item.ar)}
                                                </span>
                                            </td>

                                            {/* AP */}
                                            <td className="px-4 py-3 text-right">
                                                <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                                                    {formatCurrency(item.ap)}
                                                </span>
                                            </td>

                                            {/* Profit */}
                                            <td className="px-4 py-3 text-right">
                                                <span
                                                    className={`font-bold tabular-nums ${item.profit >= 0
                                                        ? "text-blue-600 dark:text-blue-400"
                                                        : "text-red-600 dark:text-red-400"
                                                        }`}
                                                >
                                                    {formatCurrency(item.profit)}
                                                </span>
                                            </td>

                                            {/* Margin */}
                                            <td className="px-4 py-3 text-right">
                                                <span
                                                    className={`font-bold tabular-nums ${item.margin >= 0
                                                        ? "text-purple-600 dark:text-purple-400"
                                                        : "text-red-600 dark:text-red-400"
                                                        }`}
                                                >
                                                    {item.margin.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}

                                    {chargeMarginAnalysis.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                                            >
                                                No charges to analyze
                                            </td>
                                        </tr>
                                    )}
                                </tbody>

                                {chargeMarginAnalysis.length > 0 && (
                                    <tfoot>
                                        <tr
                                            className="
                            border-t-2 border-slate-200 dark:border-slate-700
                            bg-slate-50/70 dark:bg-slate-800/40
                            backdrop-blur-sm
                        "
                                        >
                                            {/* Total */}
                                            <td className="px-4 py-3 font-bold tracking-wide text-slate-800 dark:text-slate-100">
                                                TOTAL
                                            </td>

                                            {/* Revenue */}
                                            <td className="px-4 py-3 text-right">
                                                <span className="font-bold tabular-nums text-amber-600 dark:text-amber-400">
                                                    {formatCurrency(totalRevenue)}
                                                </span>
                                            </td>

                                            {/* Cost */}
                                            <td className="px-4 py-3 text-right">
                                                <span className="font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                                                    {formatCurrency(totalCost)}
                                                </span>
                                            </td>

                                            {/* Profit */}
                                            <td className="px-4 py-3 text-right">
                                                <span
                                                    className={`font-extrabold tabular-nums ${grossProfit >= 0
                                                        ? "text-blue-600 dark:text-blue-400"
                                                        : "text-red-600 dark:text-red-400"
                                                        }`}
                                                >
                                                    {formatCurrency(grossProfit)}
                                                </span>
                                            </td>

                                            {/* Margin */}
                                            <td className="px-4 py-3 text-right">
                                                <span
                                                    className={`font-extrabold tabular-nums ${profitMargin >= 0
                                                        ? "text-purple-600 dark:text-purple-400"
                                                        : "text-red-600 dark:text-red-400"
                                                        }`}
                                                >
                                                    {profitMargin.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div>

                {/* Charge Selection Dialog */}
                {!!open && selectedParty && (
                    <ChargeSelectionDialog
                        open={!!open}
                        onOpenChange={() => {
                            setOpen("");
                            setSelectedParty(null);
                        }}
                        type={open === "AR" ? "invoice" : "bill"}
                        partyName={open === "AR" ? selectedParty.partyName : selectedParty.vendorName}
                        charges={selectedParty.charges}
                        onProceed={handleProceedToAccounting}
                    />
                )}

                {/* Unlink Confirmation Dialog */}
                <AlertDialog open={unlinkDialog.open} onOpenChange={(open) => !open && setUnlinkDialog({ open: false, charge: null, type: null })}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-amber-500" />
                                Unlink {unlinkDialog.type === "AR" ? "Invoice" : "Bill"}?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                This will remove the link between this charge and {unlinkDialog.type === "AR" ? "invoice" : "bill"}{" "}
                                <strong>{unlinkDialog.charge?.invoiceNo || unlinkDialog.charge?.billNo}</strong>.
                                The charge status will be reset to "Estimated".
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleUnlink} className="bg-red-500 hover:bg-red-600">
                                Unlink
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </TooltipProvider>
    );
};

export default JobCosting;
